// frontend/src/modules/onboarding/OnboardingAutoAdvance.tsx
//
// Headless (renders nothing). Owns the two effects that must survive the
// walkthrough navigating away from the dashboard home:
//
//   1. first-run auto-start — open the first unfinished walkthrough, once
//   2. auto-advance — while a tour is running, a route change means the user may
//      have just done the thing; re-check and close the tour if so
//
// Both used to live in <GetStartedPanel>, which is mounted by HomePage — the
// /dashboard INDEX route. Every tour stop routes somewhere else, so the first
// navigation unmounted the panel and took these effects with it: the overlay
// then sat there until the user pressed Done or Escape, and the spec's
// "guided but never blocking" mechanism never ran at all. Mounted inside
// <TourProvider> in App.tsx, this lives as long as the dashboard subtree does.

import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useOnboarding } from './OnboardingProvider';
import { useTour } from './TourProvider';
import { STEP_PRESENTATION } from './registry';
import { onboardingApi } from './api';

export function OnboardingAutoAdvance(): null {
  const { state, refresh } = useOnboarding();
  const tour = useTour();
  const location = useLocation();

  const steps = state?.steps ?? [];

  // On a user's very first load, open the first unfinished walkthrough so the
  // experience is guided rather than a checklist they have to notice. GETs no
  // longer consume the one-shot flag — the server stamps first_run_at only
  // when we report the walkthrough actually opened (firstRunSeen below), so a
  // load that never showed a tour can't burn the auto-open, and StrictMode's
  // double-GET in dev is harmless. The ref guards double-invoked effects.
  const autoStarted = React.useRef(false);
  React.useEffect(() => {
    if (autoStarted.current || !state?.first_run) return;
    // Skip past steps that have no walkthrough (stops: []) — bailing on them
    // used to permanently disable auto-open for users whose first unfinished
    // step happened to be presentation-less.
    const firstUnfinished = steps.find(
      (s) => !s.done && (STEP_PRESENTATION[s.key]?.stops.length ?? 0) > 0,
    );
    if (!firstUnfinished) return;
    autoStarted.current = true;
    tour.start(STEP_PRESENTATION[firstUnfinished.key].stops);
    // Only now is the one-shot spent. Best-effort: a failed report just means
    // the tour may auto-open once more on the next load, which is benign.
    void onboardingApi.firstRunSeen().catch(() => undefined);
  }, [state?.first_run, steps, tour]);

  // Guarded on tour.active so an idle dashboard never polls.
  const touredKey = React.useMemo(
    () => Object.values(STEP_PRESENTATION)
      .find((p) => p.stops.some((st) => st.anchor === tour.current?.anchor))?.key,
    [tour.current?.anchor],
  );

  React.useEffect(() => {
    if (!tour.active || !touredKey) return;
    let cancelled = false;
    void refresh().then((next) => {
      if (cancelled || !next) return;
      if (next.steps.some((s) => s.key === touredKey && s.done)) tour.stop();
    });
    return () => { cancelled = true; };
  }, [location.pathname, tour.active, touredKey, refresh, tour.stop]);

  return null;
}
